/**
 * Document index dropdown shared by the PDF viewers (Medical Necessity, Final
 * Documents).
 *
 * Renders a list-icon toggle (styled to match the PDF toolbar buttons) that
 * opens a floating panel of the order's source documents (from
 * `document_page_mapping`). Each row shows the document name on the left and
 * its (humanized) type on the right; clicking a row jumps the PDF to that
 * document's first page.
 *
 * The panel is portalled to the nearest `dialog` (so it stays above native
 * modal dialogs, which live in the top layer) or `document.body`, and
 * positioned from the button's viewport rect so it floats above the viewer
 * without being clipped by the renderer's `overflow: hidden` root. Those
 * coordinates are rebased onto the nearest ancestor that acts as a containing
 * block for fixed elements — `dialog.custom-modal.show` in `index.css` is
 * centred with a `transform`, which makes `position: fixed` resolve against
 * the dialog rather than the viewport. Figma-specific dimensions use inline
 * styles because Tailwind's JIT does not reliably regenerate utility classes
 * for `.tsx` edits in this project's build.
 * @module DocumentMappingList
 */
import React, {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { DocumentPageMapping } from "../../data-model/medicalPaOrdersModel";
import { formatDocType } from "./documentTypeLabel";

/** Props for {@link DocumentMappingList}. */
export interface DocumentMappingListProps {
  /** Source-document page ranges for the merged PDF. */
  mappings: DocumentPageMapping[];
  /** Invoked with the 1-based page to scroll the PDF to. */
  onSelectPage: (page: number) => void;
  /**
   * Selector of the PDF panel the floating card is sized against. Falls back
   * to a fixed width when omitted or when no ancestor matches.
   */
  containerSelector?: string;
  /** Invoked when the report list is opened. */
  onListOpen?: () => void;
  /** Invoked when a report row is selected. */
  onReportSelected?: (mapping: DocumentPageMapping) => void;
}

/**
 * Position + width for the floating panel, relative to whichever box its
 * `position: fixed` resolves against (the viewport, or the portal target's
 * containing block when one exists).
 */
export interface PanelPosition {
  top: number;
  left: number;
  width: number;
}

/** Gap in px between the toggle button and the floating panel. */
export const PANEL_GAP = 6;

/** Panel width as a fraction of the PDF container width. */
export const WIDTH_RATIO = 0.6;

/** Fallback panel width (px) when the PDF container can't be measured. */
export const FALLBACK_WIDTH = 440;

/** Floating card container styles (mirrors the Figma spec). */
const PANEL_STYLE: CSSProperties = {
  position: "fixed",
  zIndex: 1000,
  display: "flex",
  padding: "15px 17px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",
  maxHeight: "70vh",
  overflowY: "auto",
  overflowX: "hidden",
  borderRadius: "6px",
  border: "1px solid #E6E6E6",
  background: "#FFF",
  boxShadow:
    "0 20px 25px -5px rgba(0, 0, 0, 0.10), 0 8px 10px -6px rgba(0, 0, 0, 0.10)",
};

/** Row styles for a single document entry. */
const ROW_STYLE: CSSProperties = {
  display: "flex",
  height: "24px",
  width: "100%",
  justifyContent: "space-between",
  alignItems: "center",
  alignSelf: "stretch",
  gap: "12px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
};

/** Left-aligned document-name text styles (truncates on overflow). */
const NAME_STYLE: CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: "Lato",
  fontSize: "0.75rem",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "1.125rem",
  color: "#0F0F0F",
};

/** Right-aligned document-type pill styles. */
const TYPE_STYLE: CSSProperties = {
  flexShrink: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  padding: "2px 6px",
  borderRadius: "4px",
  background: "#F5F5F5",
  whiteSpace: "nowrap",
  fontFamily: "Lato",
  fontSize: "0.6875rem",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "1.125rem",
  color: "#0F0F0F",
};

/**
 * Resolves where the floating panel should be portalled for a given toggle.
 *
 * Native `<dialog>`s opened with `showModal()` sit in the top layer, so a
 * body-level portal would render behind them regardless of `z-index`.
 *
 * @param button - The toggle button, or null before it mounts
 * @returns The nearest ancestor dialog, or `document.body`
 */
export const resolvePortalTarget = (button: HTMLElement | null): HTMLElement =>
  button?.closest("dialog") ?? document.body;

/**
 * Finds the ancestor (or the node itself) that acts as the containing block
 * for `position: fixed` descendants.
 *
 * A `transform`, `perspective`, `filter`, `contain`, or the matching
 * `will-change` hints take a fixed element out of the viewport coordinate
 * space and anchor it to that ancestor's padding box instead.
 *
 * @param node - Element to start the upward walk from
 * @returns The containing-block element, or null when fixed resolves against
 *   the viewport
 */
export const findFixedContainingBlock = (
  node: Element | null,
): Element | null => {
  /** True when a computed value is set to something other than its default. */
  const isSet = (value: string) => Boolean(value) && value !== "none";

  for (let element = node; element; element = element.parentElement) {
    const style = window.getComputedStyle(element);
    const anchorsFixedChildren =
      isSet(style.transform) ||
      isSet(style.perspective) ||
      isSet(style.filter) ||
      /paint|layout|strict|content/.test(style.contain ?? "") ||
      /transform|perspective|filter/.test(style.willChange ?? "");
    if (anchorsFixedChildren) return element;
  }
  return null;
};

/**
 * Converts a viewport point into coordinates the floating panel can use once
 * portalled into `portalTarget`.
 *
 * @param point - Desired top/left of the panel in viewport coordinates
 * @param portalTarget - Element the panel is portalled into
 * @returns The equivalent top/left for the panel's `position: fixed` box
 */
export const rebaseToPortalTarget = (
  point: { top: number; left: number },
  portalTarget: HTMLElement,
): { top: number; left: number } => {
  const host = findFixedContainingBlock(portalTarget);
  if (!host) return point;

  const hostRect = host.getBoundingClientRect();
  const hostStyle = window.getComputedStyle(host);
  return {
    top: point.top - hostRect.top - parseFloat(hostStyle.borderTopWidth || "0"),
    left:
      point.left - hostRect.left - parseFloat(hostStyle.borderLeftWidth || "0"),
  };
};

/**
 * List / index icon shown on the toggle button.
 *
 * @returns The rendered SVG icon
 */
const ListIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" />
  </svg>
);

/**
 * Toggle + floating dropdown listing the order's source documents.
 *
 * Manages its own open state, repositions on scroll/resize, and closes on
 * outside click. Renders nothing when there are no mappings.
 *
 * @param props - Document mappings, page-select callback, and sizing container
 * @returns The rendered control, or null when there is nothing to index
 */
export const DocumentMappingList: React.FC<DocumentMappingListProps> = ({
  mappings,
  onSelectPage,
  containerSelector,
  onListOpen,
  onReportSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const container = containerSelector
        ? button.closest(containerSelector)
        : null;
      const containerRect = container?.getBoundingClientRect();
      const width = containerRect
        ? containerRect.width * WIDTH_RATIO
        : FALLBACK_WIDTH;
      const { top, left } = rebaseToPortalTarget(
        { top: rect.bottom + PANEL_GAP, left: rect.left },
        resolvePortalTarget(button),
      );
      setPosition({ top, left, width });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, containerSelector]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !buttonRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (!mappings.length) return null;

  const orderedMappings = [...mappings].sort(
    (a, b) => (a.merge_order ?? 0) - (b.merge_order ?? 0),
  );

  const handleSelect = (mapping: DocumentPageMapping) => {
    const startPage = mapping.start_page;
    if (typeof startPage === "number" && startPage > 0) {
      onReportSelected?.(mapping);
      onSelectPage(startPage);
    }
    setIsOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          const next = !isOpen;
          setIsOpen(next);
          if (next) onListOpen?.();
        }}
        aria-label="Show documents"
        aria-expanded={isOpen}
        aria-pressed={isOpen}
        className="pdf-renderer__tool-btn"
      >
        <ListIcon />
      </button>

      {isOpen &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              ...PANEL_STYLE,
              top: position.top,
              left: position.left,
              width: position.width,
            }}
          >
            {orderedMappings.map((mapping, index) => (
              <button
                key={`${mapping.doc_name ?? "doc"}-${
                  mapping.start_page ?? index
                }`}
                type="button"
                onClick={() => handleSelect(mapping)}
                style={ROW_STYLE}
              >
                <span style={NAME_STYLE}>
                  {mapping.doc_name || "Untitled document"}
                </span>
                <span style={TYPE_STYLE}>
                  {formatDocType(mapping.doc_type)}
                </span>
              </button>
            ))}
          </div>,
          resolvePortalTarget(buttonRef.current),
        )}
    </>
  );
};

export default DocumentMappingList;
